import { useState, useEffect, Fragment, useMemo } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    InputGroup,
    Row,
    Spinner,
} from 'react-bootstrap';
import axios from 'axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { calculateTotal, formatMoney, parseDecimal } from './money';
import Totals from './totals';

import 'dayjs/locale/nl';

dayjs.locale('nl');

const headers = {
    Accept: 'application/json',
    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
    Authorization: 'Bearer ' + document.querySelector('meta[name="api-token"]')?.getAttribute('content'),
};

const months = [
    { value: 1, label: 'januari' },
    { value: 2, label: 'februari' },
    { value: 3, label: 'maart' },
    { value: 4, label: 'april' },
    { value: 5, label: 'mei' },
    { value: 6, label: 'juni' },
    { value: 7, label: 'juli' },
    { value: 8, label: 'augustus' },
    { value: 9, label: 'september' },
    { value: 10, label: 'oktober' },
    { value: 11, label: 'november' },
    { value: 12, label: 'december' },
];

const getDefaultPeriod = () => {
    const previousMonth = dayjs().subtract(1, 'month');

    return {
        month: previousMonth.month() + 1,
        year: previousMonth.year(),
    };
};

const loadStoredFilters = () => {
    try {
        return JSON.parse(window.localStorage.getItem('usageFilters')) ?? {};
    } catch (error) {
        return {};
    }
};

const UsageComponent = () => {
    const defaultPeriod = useMemo(() => getDefaultPeriod(), []);
    const storedFilters = useMemo(() => loadStoredFilters(), []);

    const [companies, setCompanies] = useState([]);
    const [years, setYears] = useState([]);
    const [rows, setRows] = useState([]);

    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(defaultPeriod.month);
    const [selectedYear, setSelectedYear] = useState(defaultPeriod.year);
    const [selectedVendors, setSelectedVendors] = useState(
        Array.isArray(storedFilters.selectedVendors) ? storedFilters.selectedVendors : []
    );
    const [collapsedCompanies, setCollapsedCompanies] = useState([]);

    const [companySearch, setCompanySearch] = useState(storedFilters.companySearch ?? '');
    const [resultSearch, setResultSearch] = useState(storedFilters.resultSearch ?? '');
    const [onlyWithUsage, setOnlyWithUsage] = useState(Boolean(storedFilters.onlyWithUsage));
    const [companiesLoading, setCompaniesLoading] = useState(false);
    const [usageLoading, setUsageLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadCompanies = async () => {
            setCompaniesLoading(true);
            setError('');

            try {
                const response = await axios.get('/api/v1/usage/companies', { headers });
                setCompanies(response.data || []);
            } catch (error) {
                console.error(error);
                setError('Bedrijven konden niet worden opgehaald.');
            } finally {
                setCompaniesLoading(false);
            }
        };

        loadCompanies();

        const currentYear = new Date().getFullYear();
        const nextYears = [];

        for (let year = currentYear + 1; year >= 2023; year--) {
            nextYears.push(year);
        }

        setYears(nextYears);
    }, []);

    useEffect(() => {
        if (selectedCompanies.length === 0 || !selectedMonth || !selectedYear) {
            setRows([]);
            return;
        }

        const controller = new AbortController();

        const loadUsage = async () => {
            setUsageLoading(true);
            setError('');

            try {
                const response = await axios.get(
                    `/api/v1/usage/${selectedCompanies.join(',')}/${selectedMonth}/${selectedYear}`,
                    {
                        headers,
                        signal: controller.signal,
                    }
                );

                setRows(response.data || []);
            } catch (error) {
                if (axios.isCancel?.(error) || error.name === 'CanceledError') {
                    return;
                }

                console.error(error);
                setRows([]);
                setError('Verbruiksgegevens konden niet worden opgehaald.');
            } finally {
                setUsageLoading(false);
            }
        };

        loadUsage();

        return () => {
            controller.abort();
        };
    }, [selectedCompanies, selectedMonth, selectedYear]);

    useEffect(() => {
        try {
            window.localStorage.setItem('usageFilters', JSON.stringify({
                companySearch,
                resultSearch,
                onlyWithUsage,
                selectedVendors,
            }));
        } catch (error) {
            // Filtering remains functional when browser storage is unavailable.
        }
    }, [companySearch, resultSearch, onlyWithUsage, selectedVendors]);

    const filteredCompanies = useMemo(() => {
        const search = companySearch.trim().toLowerCase();

        if (!search) {
            return companies;
        }

        return companies.filter((company) =>
            company.name?.toLowerCase().includes(search)
        );
    }, [companies, companySearch]);

    const selectedCompanyObjects = useMemo(() => {
        return companies.filter((company) =>
            selectedCompanies.includes(String(company.id))
        );
    }, [companies, selectedCompanies]);

    const selectedMonthLabel = useMemo(() => {
        return months.find((month) => Number(month.value) === Number(selectedMonth))?.label;
    }, [selectedMonth]);

    const vendors = useMemo(() => {
        return Array.from(new Set(
            rows.flatMap((row) => (row.usage ?? []).map((usage) => usage.vendor).filter(Boolean))
        )).sort((left, right) => left.localeCompare(right));
    }, [rows]);

    const filteredRows = useMemo(() => {
        const search = resultSearch.trim().toLowerCase();

        return rows
            .map((row) => {
                const companyMatches = Boolean(search) && row.company?.toLowerCase().includes(search);
                const usage = (row.usage ?? []).filter((record) => {
                    if (selectedVendors.length > 0 && !selectedVendors.includes(record.vendor)) {
                        return false;
                    }

                    if (onlyWithUsage && parseDecimal(record.quantity) === 0) {
                        return false;
                    }

                    return !search
                        || companyMatches
                        || record.description?.toLowerCase().includes(search);
                });

                return { ...row, usage };
            })
            .filter((row) => row.usage.length > 0);
    }, [rows, selectedVendors, resultSearch, onlyWithUsage]);

    const activeFilterCount = useMemo(() => [
        companySearch.trim(),
        resultSearch.trim(),
        selectedVendors.length > 0,
        onlyWithUsage,
    ].filter(Boolean).length, [companySearch, resultSearch, selectedVendors, onlyWithUsage]);

    const usageSummary = useMemo(() => {
        return filteredRows.reduce((summary, row) => {
            (row.usage ?? []).forEach((usage) => {
                summary.lines += 1;
                summary.quantity += parseDecimal(usage.quantity);
                summary.total += calculateTotal(usage.price, usage.quantity, usage.total);
            });

            return summary;
        }, { lines: 0, quantity: 0, total: 0 });
    }, [filteredRows]);

    const toggleCompany = (companyId) => {
        const id = String(companyId);

        setSelectedCompanies((current) => {
            if (current.includes(id)) {
                return current.filter((selectedId) => selectedId !== id);
            }

            return [...current, id];
        });
    };

    const selectVisibleCompanies = () => {
        const visibleIds = filteredCompanies.map((company) => String(company.id));

        setSelectedCompanies((current) => {
            const merged = new Set([...current, ...visibleIds]);
            return Array.from(merged);
        });
    };

    const clearSelectedCompanies = () => {
        setSelectedCompanies([]);
        setRows([]);
    };

    const removeSelectedCompany = (companyId) => {
        const id = String(companyId);

        setSelectedCompanies((current) =>
            current.filter((selectedId) => selectedId !== id)
        );
    };

    const toggleVendor = (vendor) => {
        setSelectedVendors((current) => current.includes(vendor)
            ? current.filter((selectedVendor) => selectedVendor !== vendor)
            : [...current, vendor]
        );
    };

    const clearAllFilters = () => {
        setCompanySearch('');
        setResultSearch('');
        setOnlyWithUsage(false);
        setSelectedVendors([]);
    };

    const exportFilename = `verbruik-${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

    const buildExportRows = () => filteredRows.flatMap((row) =>
        (row.usage ?? []).map((usage) => ({
            Bedrijf: row.company,
            Leverancier: usage.vendor ?? '',
            Licentie: usage.description ?? '',
            Van: renderDate(usage.from_date),
            Tot: renderDate(usage.to_date),
            Dagen: parseDecimal(usage.days_count),
            Prijs: parseDecimal(usage.price),
            Aantal: parseDecimal(usage.quantity),
            Totaal: Number(calculateTotal(usage.price, usage.quantity, usage.total).toFixed(2)),
        }))
    );

    const downloadBlob = (content, type, extension) => {
        const url = URL.createObjectURL(new Blob([content], { type }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${exportFilename}.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const exportCsv = () => {
        const exportRows = buildExportRows();
        const columns = Object.keys(exportRows[0]);
        const escapeCell = (value) => {
            const normalized = typeof value === 'number'
                ? String(value).replace('.', ',')
                : String(value ?? '');

            return `"${normalized.replace(/"/g, '""')}"`;
        };
        const csv = [
            columns.map(escapeCell).join(';'),
            ...exportRows.map((row) => columns.map((column) => escapeCell(row[column])).join(';')),
        ].join('\r\n');

        downloadBlob(`\uFEFF${csv}`, 'text/csv;charset=utf-8', 'csv');
    };

    const exportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(buildExportRows());
        worksheet['!cols'] = [
            { wch: 28 }, { wch: 18 }, { wch: 42 }, { wch: 12 }, { wch: 12 },
            { wch: 9 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Verbruik');
        XLSX.writeFile(workbook, `${exportFilename}.xlsx`);
    };

    const printReport = () => {
        setCollapsedCompanies([]);
        window.setTimeout(() => window.print(), 0);
    };

    const isCompanyCollapsed = (company) => collapsedCompanies.includes(company);

    const toggleCompanyGroup = (company) => {
        setCollapsedCompanies((current) => current.includes(company)
            ? current.filter((collapsedCompany) => collapsedCompany !== company)
            : [...current, company]
        );
    };

    const openAllCompanyGroups = () => setCollapsedCompanies([]);

    const closeAllCompanyGroups = () => {
        setCollapsedCompanies(filteredRows.map((row) => row.company));
    };

    const renderDate = (value) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const stringValue = String(value).trim();

        // Unix timestamp in seconds, example: 1711922400
        if (/^\d{10}$/.test(stringValue)) {
            return dayjs.unix(Number(stringValue)).format('DD-MM-YYYY');
        }

        // Unix timestamp in milliseconds, example: 1711922400000
        if (/^\d{13}$/.test(stringValue)) {
            return dayjs(Number(stringValue)).format('DD-MM-YYYY');
        }

        // Normal date string, example: 2026-04-01 or 2026-04-01 00:00:00
        const parsedDate = dayjs(stringValue);

        if (parsedDate.isValid()) {
            return parsedDate.format('DD-MM-YYYY');
        }

        return '';
    };

    const renderSubRow = (usage, index) => {
        const priceNumber = parseDecimal(usage.price);
        const quantityNumber = parseDecimal(usage.quantity);

        const price = formatMoney(priceNumber, 2);

        const quantity = quantityNumber < 0 || !Number.isInteger(quantityNumber)
            ? quantityNumber.toFixed(2)
            : quantityNumber.toFixed(0);

        const total = calculateTotal(usage.price, usage.quantity, usage.total);

        const daysInMonth = dayjs()
            .month(Number(selectedMonth) - 1)
            .year(Number(selectedYear))
            .daysInMonth();

        let extraText = '';

        if (Number(daysInMonth) !== Number(usage.days_count)) {
            extraText = ` (${usage.days_count} dagen)`;
        }

        const fromDate = renderDate(usage.from_date);
        const toDate = renderDate(usage.to_date);

        return (
            <tr key={`${usage.id ?? index}-row-usage`}>
                <td>
                    <div className="fw-semibold">{usage.description}</div>

                    {extraText && (
                        <div>
                            <small className="text-muted">{extraText}</small>
                        </div>
                    )}

                    {fromDate && toDate && (
                        <div>
                            <small className="text-muted">
                                {fromDate} / {toDate}
                            </small>
                        </div>
                    )}
                </td>

                <td className="text-nowrap">{price} p/st</td>
                <td className="text-end">{quantity}</td>
                <td className="text-end fw-semibold">{formatMoney(total, 2)}</td>
            </tr>
        );
    };

    const renderRow = (row, index) => {
        const collapsed = isCompanyCollapsed(row.company);

        return (
            <Fragment key={`${row.company}-${index}`}>
                <tr className="bg-primary-subtle">
                    <td colSpan={4} className="bg-primary-subtle">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <Button
                                type="button"
                                variant="link"
                                className="usage-company-toggle p-0 text-start text-decoration-none"
                                aria-expanded={!collapsed}
                                onClick={() => toggleCompanyGroup(row.company)}
                            >
                                <i className={`fa-solid fa-chevron-${collapsed ? 'right' : 'down'} me-2`} aria-hidden="true" />
                                <span className="fw-semibold">{row.company}</span>
                            </Button>
                            <Badge bg="primary" pill>{row.usage?.length ?? 0} regels</Badge>
                        </div>
                    </td>
                </tr>

                {!collapsed && (row.usage ?? []).map((usage, i) => renderSubRow(usage, i))}
            </Fragment>
        );
    };

    const renderMobileRow = (row, index) => {
        const usages = row.usage ?? [];
        const collapsed = isCompanyCollapsed(row.company);
        const companyTotal = usages.reduce(
            (total, usage) => total + calculateTotal(usage.price, usage.quantity, usage.total),
            0
        );

        return (
            <section className="usage-mobile-company" key={`${row.company}-${index}-mobile`}>
                <header className="usage-mobile-company-header">
                    <Button
                        type="button"
                        variant="link"
                        className="usage-company-toggle p-0 text-start text-decoration-none"
                        aria-expanded={!collapsed}
                        onClick={() => toggleCompanyGroup(row.company)}
                    >
                        <i className={`fa-solid fa-chevron-${collapsed ? 'right' : 'down'} me-2`} aria-hidden="true" />
                        <span className="fw-semibold text-break">{row.company}</span>
                    </Button>
                    <Badge bg="primary" pill>{usages.length} regels</Badge>
                </header>

                {!collapsed && <div className="usage-mobile-lines">
                    {usages.map((usage, usageIndex) => {
                        const quantity = parseDecimal(usage.quantity);
                        const formattedQuantity = quantity < 0 || !Number.isInteger(quantity)
                            ? quantity.toFixed(2)
                            : quantity.toFixed(0);
                        const fromDate = renderDate(usage.from_date);
                        const toDate = renderDate(usage.to_date);
                        const daysInMonth = dayjs()
                            .month(Number(selectedMonth) - 1)
                            .year(Number(selectedYear))
                            .daysInMonth();
                        const partialPeriod = Number(daysInMonth) !== Number(usage.days_count);

                        return (
                            <article className="usage-mobile-line" key={`${usage.id ?? usageIndex}-mobile-usage`}>
                                <div className="fw-semibold text-break">{usage.description}</div>

                                {(partialPeriod || (fromDate && toDate)) && (
                                    <div className="small text-muted mt-1">
                                        {fromDate && toDate && <span>{fromDate} / {toDate}</span>}
                                        {partialPeriod && <span>{fromDate && toDate ? ' · ' : ''}{usage.days_count} dagen</span>}
                                    </div>
                                )}

                                <dl className="usage-mobile-values mb-0 mt-3">
                                    <div>
                                        <dt>Aantal</dt>
                                        <dd>{formattedQuantity}</dd>
                                    </div>
                                    <div>
                                        <dt>Prijs</dt>
                                        <dd>{formatMoney(parseDecimal(usage.price), 2)} p/st</dd>
                                    </div>
                                    <div>
                                        <dt>Totaal</dt>
                                        <dd>{formatMoney(calculateTotal(usage.price, usage.quantity, usage.total), 2)}</dd>
                                    </div>
                                </dl>
                            </article>
                        );
                    })}
                </div>}

                {!collapsed && <footer className="usage-mobile-company-total">
                    <span>Bedrijfstotaal</span>
                    <strong>{formatMoney(companyTotal, 2)}</strong>
                </footer>}
            </section>
        );
    };

    return (
        <Fragment>
            <Card className="mt-3 border-0 shadow-sm">
                <Card.Body>
                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                        <div>
                            <h5 className="mb-1">Verbruiksoverzicht</h5>
                            <div className="text-muted">
                                Selecteer één of meerdere bedrijven en kies de periode.
                            </div>
                        </div>

                        {selectedCompanies.length > 0 && (
                            <Badge bg="primary" className="fs-6">
                                {selectedCompanies.length} geselecteerd
                            </Badge>
                        )}
                    </div>

                    {error && (
                        <Alert variant="danger" className="mb-3">
                            {error}
                        </Alert>
                    )}

                    <Form as={Row} className="g-3">
                        <Col lg={6}>
                            <Form.Group controlId="company">
                                <Form.Label>Bedrijf</Form.Label>

                                <InputGroup className="mb-2">
                                    <Form.Control
                                        type="search"
                                        placeholder="Zoek op bedrijfsnaam..."
                                        value={companySearch}
                                        disabled={companiesLoading || usageLoading}
                                        onChange={(e) => setCompanySearch(e.target.value)}
                                    />

                                    {companySearch && (
                                        <Button
                                            variant="outline-secondary"
                                            disabled={companiesLoading || usageLoading}
                                            onClick={() => setCompanySearch('')}
                                        >
                                            Wissen
                                        </Button>
                                    )}
                                </InputGroup>

                                <div className="d-flex flex-wrap gap-2 mb-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        type="button"
                                        onClick={selectVisibleCompanies}
                                        disabled={companiesLoading || usageLoading || filteredCompanies.length === 0}
                                    >
                                        Selecteer zichtbaar
                                    </Button>

                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        type="button"
                                        onClick={clearSelectedCompanies}
                                        disabled={companiesLoading || usageLoading || selectedCompanies.length === 0}
                                    >
                                        Selectie wissen
                                    </Button>
                                </div>

                                <div
                                    className="border rounded bg-body"
                                    style={{
                                        maxHeight: 260,
                                        overflowY: 'auto',
                                    }}
                                >
                                    {companiesLoading && (
                                        <div className="p-3 text-muted d-flex align-items-center gap-2">
                                            <Spinner animation="border" size="sm" />
                                            Bedrijven laden...
                                        </div>
                                    )}

                                    {!companiesLoading && filteredCompanies.length === 0 && (
                                        <div className="p-3 text-muted">
                                            Geen bedrijven gevonden.
                                        </div>
                                    )}

                                    {!companiesLoading && filteredCompanies.map((company) => {
                                        const isChecked = selectedCompanies.includes(String(company.id));

                                        return (
                                            <label
                                                key={company.id}
                                                className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Form.Check
                                                    id={`usage-company-${company.id}`}
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={usageLoading}
                                                    onChange={() => toggleCompany(company.id)}
                                                />

                                                <span className={isChecked ? 'fw-semibold' : ''}>
                                                    {company.name}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>

                                {selectedCompanyObjects.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {selectedCompanyObjects.map((company) => (
                                            <Badge
                                                key={company.id}
                                                bg="secondary"
                                                className="d-inline-flex align-items-center gap-2 px-2 py-2"
                                            >
                                                {company.name}

                                                <button
                                                    type="button"
                                                    className="btn-close btn-close-white"
                                                    aria-label={`Verwijder ${company.name}`}
                                                    disabled={usageLoading}
                                                    style={{ fontSize: '0.65rem' }}
                                                    onClick={() => removeSelectedCompany(company.id)}
                                                />
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={3}>
                            <Form.Group controlId="month">
                                <Form.Label>Maand</Form.Label>
                                <Form.Select
                                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                    value={selectedMonth}
                                    disabled={usageLoading}
                                >
                                    <option value="">Selecteer een maand</option>
                                    {months.map((month) => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={6} lg={3}>
                            <Form.Group controlId="year">
                                <Form.Label>Jaar</Form.Label>
                                <Form.Select
                                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                                    value={selectedYear}
                                    disabled={usageLoading}
                                >
                                    <option value="">Kies een jaar</option>
                                    {years.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Form>

                    {vendors.length > 0 && (
                        <div className="mt-4 pt-3 border-top">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                <Form.Label className="mb-0">Leveranciers</Form.Label>
                                {selectedVendors.length > 0 && (
                                    <Button variant="link" size="sm" className="p-0" disabled={usageLoading} onClick={() => setSelectedVendors([])}>
                                        Toon alle leveranciers
                                    </Button>
                                )}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {vendors.map((vendor) => {
                                    const selected = selectedVendors.length === 0 || selectedVendors.includes(vendor);

                                    return (
                                        <Button
                                            key={vendor}
                                            type="button"
                                            size="sm"
                                            variant={selected ? 'primary' : 'outline-secondary'}
                                            aria-pressed={selectedVendors.includes(vendor)}
                                            disabled={usageLoading}
                                            onClick={() => toggleVendor(vendor)}
                                        >
                                            {vendor}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div className="usage-filter-panel mt-4 pt-3 border-top">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Label className="mb-0">Resultaten filteren</Form.Label>
                                    {activeFilterCount > 0 && (
                                        <Badge bg="primary" pill>{activeFilterCount} actief</Badge>
                                    )}
                                </div>
                                <Button
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    disabled={activeFilterCount === 0}
                                    aria-label={`Alle filters wissen, ${activeFilterCount} actief`}
                                    onClick={clearAllFilters}
                                >
                                    Alle filters wissen
                                </Button>
                            </div>

                            <Row className="g-3 align-items-end">
                                <Col md={7} lg={6}>
                                    <Form.Group controlId="usage-result-search">
                                        <Form.Label className="small">Bedrijf of licentie</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text aria-hidden="true">
                                                <i className="fa-solid fa-magnifying-glass" />
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="search"
                                                value={resultSearch}
                                                placeholder="Zoek op bedrijfs- of licentienaam..."
                                                disabled={usageLoading}
                                                onChange={(event) => setResultSearch(event.target.value)}
                                            />
                                            {resultSearch && (
                                                <Button
                                                    type="button"
                                                    variant="outline-secondary"
                                                    onClick={() => setResultSearch('')}
                                                >
                                                    Wissen
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                                <Col md={5} lg={6}>
                                    <Form.Check
                                        type="switch"
                                        id="usage-only-with-usage"
                                        label="Alleen regels met verbruik"
                                        checked={onlyWithUsage}
                                        disabled={usageLoading}
                                        onChange={(event) => setOnlyWithUsage(event.target.checked)}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Row className="mt-3">
                <Col>
                    <Card className="usage-print-report border-0 shadow-sm">
                        <Card.Body className="p-0" aria-busy={usageLoading}>
                            <div className="usage-print-heading">
                                <h1>Maandelijks verbruiksrapport</h1>
                                <div>{selectedMonthLabel} {selectedYear}</div>
                            </div>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 p-3 border-bottom">
                                <div className="usage-result-heading d-flex flex-wrap align-items-center gap-2">
                                    <div className="fw-semibold">Resultaat</div>
                                    <div className="text-muted small usage-result-period">
                                        {selectedCompanies.length === 0
                                            ? 'Selecteer eerst één of meerdere bedrijven.'
                                            : `${selectedCompanies.length} bedrijf/bedrijven voor ${selectedMonthLabel} ${selectedYear}`}
                                    </div>
                                </div>

                                {usageLoading ? (
                                    <div className="text-muted d-flex align-items-center gap-2" role="status" aria-live="polite">
                                        <Spinner animation="border" size="sm" />
                                        Verbruik laden...
                                    </div>
                                ) : filteredRows.length > 0 && (
                                    <div className="usage-export-actions d-flex flex-wrap gap-2">
                                        <Button type="button" variant="outline-secondary" size="sm" onClick={exportCsv}>
                                            <i className="fa-solid fa-file-csv me-2" aria-hidden="true" />CSV
                                        </Button>
                                        <Button type="button" variant="outline-secondary" size="sm" onClick={exportExcel}>
                                            <i className="fa-solid fa-file-excel me-2" aria-hidden="true" />Excel
                                        </Button>
                                        <Button type="button" variant="outline-secondary" size="sm" onClick={printReport}>
                                            <i className="fa-solid fa-print me-2" aria-hidden="true" />Afdrukken
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {usageLoading && (
                                <div className="usage-loading-skeleton" role="status" aria-live="polite">
                                    <span className="visually-hidden">Verbruiksgegevens worden geladen.</span>
                                    <div className="usage-skeleton-summary" aria-hidden="true">
                                        {[0, 1, 2].map((item) => <span className="usage-skeleton-block" key={`summary-${item}`} />)}
                                    </div>
                                    <div className="usage-skeleton-table" aria-hidden="true">
                                        <span className="usage-skeleton-line usage-skeleton-line-heading" />
                                        {[0, 1, 2, 3, 4].map((item) => <span className="usage-skeleton-line" key={`line-${item}`} />)}
                                    </div>
                                </div>
                            )}

                            {selectedCompanies.length === 0 && (
                                <div className="usage-empty-state text-muted">
                                    <i className="fa-duotone fa-solid fa-buildings fa-2x mb-3 text-primary" />
                                    <div className="fw-semibold text-body">Selecteer bedrijven</div>
                                    <div className="small mt-1">Kies één of meerdere bedrijven om verbruik te bekijken.</div>
                                </div>
                            )}

                            {selectedCompanies.length > 0 && !usageLoading && rows.length === 0 && (
                                <div className="usage-empty-state text-muted">
                                    <i className="fa-duotone fa-solid fa-receipt fa-2x mb-3 text-primary" />
                                    <div className="fw-semibold text-body">Geen verbruik gevonden</div>
                                    <div className="small mt-1">Er zijn geen regels voor deze bedrijven en periode.</div>
                                </div>
                            )}

                            {!usageLoading && rows.length > 0 && filteredRows.length === 0 && (
                                <div className="usage-empty-state text-muted">
                                    <i className="fa-solid fa-filter-circle-xmark fa-2x mb-3 text-primary" />
                                    <div className="fw-semibold text-body">Geen resultaten</div>
                                    <div className="small mt-1">Pas de actieve filters aan om meer regels te tonen.</div>
                                    <Button type="button" variant="outline-primary" size="sm" className="mt-3" onClick={clearAllFilters}>
                                        Alle filters wissen
                                    </Button>
                                </div>
                            )}

                            {!usageLoading && filteredRows.length > 0 && (
                                <Fragment>
                                    <div className="usage-summary-grid p-3 border-bottom">
                                        <div className="usage-summary-card"><div className="small text-muted">Verbruiksregels</div><div className="fs-5 fw-bold">{usageSummary.lines}</div></div>
                                        <div className="usage-summary-card"><div className="small text-muted">Totale hoeveelheid</div><div className="fs-5 fw-bold">{usageSummary.quantity.toLocaleString('nl-NL', { maximumFractionDigits: 2 })}</div></div>
                                        <div className="usage-summary-card"><div className="small text-muted">Totaalbedrag</div><div className="fs-5 fw-bold">{formatMoney(usageSummary.total, 2)}</div></div>
                                    </div>
                                    <div className="usage-group-controls d-flex flex-wrap justify-content-end gap-2 px-3 pt-3">
                                        <Button type="button" variant="outline-secondary" size="sm" onClick={openAllCompanyGroups}>
                                            Alles openen
                                        </Button>
                                        <Button type="button" variant="outline-secondary" size="sm" onClick={closeAllCompanyGroups}>
                                            Alles sluiten
                                        </Button>
                                    </div>
                                    <div className="table-responsive usage-results-table-wrap d-none d-md-block">
                                    <table className="table table-hover border mb-0 align-middle">
                                        <caption className="visually-hidden">
                                            Verbruik per bedrijf voor {selectedMonthLabel} {selectedYear}, gefilterd op de huidige selectie.
                                        </caption>
                                        <thead className="fw-semibold text-nowrap">
                                        <tr>
                                            <th className="bg-body-secondary">Licentie</th>
                                            <th className="bg-body-secondary">Prijs</th>
                                            <th className="bg-body-secondary text-end">Aantal</th>
                                            <th className="bg-body-secondary text-end">Totaal</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {filteredRows.map((row, index) => renderRow(row, index))}
                                        </tbody>

                                        <tfoot>
                                        <Totals rows={filteredRows} />
                                        </tfoot>
                                    </table>
                                </div>
                                    <div className="usage-mobile-results d-md-none">
                                        {filteredRows.map((row, index) => renderMobileRow(row, index))}
                                    </div>
                                </Fragment>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default UsageComponent;
